'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { MutableRefObject, RefObject, useEffect, useState } from 'react'

import { EMPTY_ARROW_FUNC } from '@src/config/constants'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SiblingProductOptions } from '@src/quince-components/src/atoms/productForm/product-form-shared'
import { renderProductOption } from '@src/quince-components/src/atoms/productForm/ProductForm'
import { SiblingProductOptionType } from '@src/quince-components/src/atoms/siblingProductOptions'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOption, ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant/BaseProductVariant'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { MiniPDPCollapsibleOptionsDS } from './components/mini-pdp-collapsible-options-ds'
import { useMiniPdpSwatchLimit } from './hooks/use-mini-pdp-swatch-limit'
import { useSlideDownAnimation } from './hooks/useSlideDownAnimation'
import { buildMiniPdpOptionsSummarySegments, MINI_PDP_MERGED_ADDON_OPTION_NAME } from './miniPDPProductOptions.helpers'

// Pre-measurement fallbacks only — once the color row is measured, the limit
// derives from its actual width (see useMiniPdpSwatchLimit).
const MOBILE_SWATCH_LINE_LIMIT = 8
const DESKTOP_SWATCH_LINE_LIMIT = 11

// Option-group fieldsets bring their own marginBottom (needed in block-layout parents like the full
// PDP form); every level here spaces children with flex gap, so zero those margins to avoid doubling.
const optionsRootCss = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space-24',
  '& [data-testid="product-property"]': { marginBottom: 0 },
})

const colorShowMoreTriggerCss = css({
  color: 'mediumgray',
  flexShrink: 0,
  fontWeight: 400,
})

const addOnLabelCss = css({
  // Typography + flex centering now come from ProductPropertyDS's own header styles;
  // declaring them here too would race the block's classes in the Panda cascade.
  // NO-TOKEN: fixed 24px label box on all breakpoints (Figma); spacing tokens are not in the
  // `sizes` scale, so `space-24` would be silently dropped on minHeight
  minHeight: '24px',
})

interface MiniPDPProductOptionsDSProps {
  product: Product
  productOptions: ProductOption[]
  selectedOptions: ProductVariantOption[]
  aboveFoldTrackingRefs?: {
    colorVariantRef: RefObject<HTMLFieldSetElement>
    sizeVariantRef: RefObject<HTMLFieldSetElement>
    productInfoRef: RefObject<HTMLDivElement>
    addToCartTrackingRef: MutableRefObject<HTMLElement | null>
  }
  onOptionSelect: (optionName: string, selectedValue: string) => void
  onSiblingOptionSelect?: (val: string, productId?: number) => void
  selectedSiblingProductId?: number
  productPurchaseInfo?: IProductPurchaseInfo
  inputOptionError?: string
  sizeOptionRef?: RefObject<HTMLFieldSetElement>
  isExpandedSize: boolean
  expandSizeMiniPDP: () => void
  collapseSizeMiniPDP: () => void
}

export const MiniPDPProductOptionsDS = (props: MiniPDPProductOptionsDSProps) => {
  const { t } = useTranslation('common')
  const [isMiniPdpColorRowExpanded, setIsMiniPdpColorRowExpanded] = useState(false)
  const colorRevealRef = useSlideDownAnimation(isMiniPdpColorRowExpanded)

  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const [measureColorRowRef, swatchLineLimit] = useMiniPdpSwatchLimit(
    isMobile ? MOBILE_SWATCH_LINE_LIMIT : DESKTOP_SWATCH_LINE_LIMIT,
  )

  useEffect(() => {
    setIsMiniPdpColorRowExpanded(false)
  }, [props.product.id])

  // Legacy rendered input options (e.g. engraving) outside the collapsible, so their validation
  // errors were always visible. The accordion must open when an error appears (the error path in
  // useProductFormWrapper returns before the size branch that calls expandSizeMiniPDP) and stay
  // open until the input changes, which clears the error.
  const { inputOptionError, expandSizeMiniPDP } = props
  useEffect(() => {
    if (inputOptionError) {
      expandSizeMiniPDP()
    }
  }, [inputOptionError, expandSizeMiniPDP])

  const optionNameSelectedValueMap = props.selectedOptions.reduce((map: Record<string, string>, option) => {
    map[option.name] = option.value
    return map
  }, {})

  const productOptions = props.productOptions
    .filter(option => !option.isHidden)
    .map(productOption => {
      return {
        node: renderProductOption({
          productOption: productOption,
          selectedOptions: props.selectedOptions,
          onOptionSelect: props.onOptionSelect,
          sizeChart: undefined,
          mergedSizeChart: undefined,
          handleSizeChart: EMPTY_ARROW_FUNC,
          hideSizeChart: true,
          optionNameSelectedValueMap: optionNameSelectedValueMap,
          renderSwatchSelectionButton: () => null,
          shouldReduceMarginBottom: false,
          showInlineSizeChart: false,
          showTruefitWidget: false,
          product: props.product,
          t: t,
          productPurchaseInfo: props.productPurchaseInfo,
          inputOptionError: props.inputOptionError,
          colorVariantRef: props.aboveFoldTrackingRefs?.colorVariantRef,
          sizeVariantRef: props.aboveFoldTrackingRefs?.sizeVariantRef,
          sizeOptionRef: props.sizeOptionRef,
          ...(productOption.name === ProductOptionName.Color
            ? {
                isMiniPDP: true,
                showMoreOption: !isMiniPdpColorRowExpanded,
                optionLimit: swatchLineLimit,
                onShowMoreClick: () => {
                  setIsMiniPdpColorRowExpanded(true)
                },
                excessOptionsTextClassName: colorShowMoreTriggerCss,
              }
            : // Figma 12199:26256 — accordion rows use the ProductProperty `size=sm` header (body-12
              // on every breakpoint) rather than the full-PDP scale.
              { headerSize: 'sm' as const }),
        }),
        displaySequence: productOption.displaySequence,
        key: productOption.name,
        optionName: undefined,
      }
    })

  const addOnOptions = props.product?.addOnProductOptionGroups?.map(group => {
    return {
      node: (
        <SiblingProductOptions
          siblingProductOptionGroup={group}
          displayType={props?.onSiblingOptionSelect ? SiblingProductOptionType.BUTTON : SiblingProductOptionType.LINK}
          selectedProductId={props.selectedSiblingProductId}
          selectedProductOptions={props.selectedOptions}
          onSelect={props.onSiblingOptionSelect}
          labelClassName={addOnLabelCss}
          isMiniPDP={true}
        />
      ),
      displaySequence: group.optionDisplaySequence,
      key: group.groupId,
      optionName: MINI_PDP_MERGED_ADDON_OPTION_NAME,
    }
  })

  const combinedProductOptions = [...(addOnOptions ?? []), ...productOptions]
  combinedProductOptions.sort((a, b) => a.displaySequence - b.displaySequence)

  const accordionEntries = combinedProductOptions.filter(option => option.key !== ProductOptionName.Color)

  const accordionSummarySegments = buildMiniPdpOptionsSummarySegments(
    props.product,
    props.selectedSiblingProductId,
    optionNameSelectedValueMap,
    accordionEntries.map(e => ({
      key: e.key,
      optionName: e.optionName,
      displaySequence: e.displaySequence,
    })),
  )

  const handleToggle = (nextExpanded: boolean) => {
    if (nextExpanded) {
      props.expandSizeMiniPDP()
    } else if (!props.inputOptionError) {
      props.collapseSizeMiniPDP()
    }
  }

  return (
    <div data-testid={TEST_LOCATORS.MINI_PDP.PRODUCT_OPTIONS} className={optionsRootCss}>
      {combinedProductOptions
        .filter(option => option.key === ProductOptionName.Color)
        .map(option => (
          <div
            key={option.key}
            ref={colorRevealRef}
            data-testid={TEST_LOCATORS.MINI_PDP.OPTION_GROUP(String(option.key))}
          >
            {/* Separate measure target: colorRevealRef is a plain RefObject, so the
                width observer rides its own callback-ref wrapper instead. */}
            <div ref={measureColorRowRef}>{option.node}</div>
          </div>
        ))}
      {accordionEntries.length > 0 && (
        <MiniPDPCollapsibleOptionsDS
          summarySegments={accordionSummarySegments}
          emptySummaryLabel={t('please_select_an_option')}
          isExpanded={props.isExpandedSize}
          onToggle={handleToggle}
        >
          {accordionEntries.map(option => (
            <div key={String(option.key)} data-testid={TEST_LOCATORS.MINI_PDP.OPTION_GROUP(String(option.key))}>
              {option.node}
            </div>
          ))}
        </MiniPDPCollapsibleOptionsDS>
      )}
    </div>
  )
}

export default MiniPDPProductOptionsDS
